import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  fileIntoProject,
  UnconfirmedDocumentRegistrationError,
} from "@/domains/documents/services/fileIntoProject";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import type { PickedFolder } from "@/components/chat/SidebarChat/FolderPickerDialog";
import type { ProtocolSavePhase } from "./protocolSaveTypes";

export interface UseProtocolSaveResult {
  phase: ProtocolSavePhase;
  /** Files `file` into the picked destination. Never throws — every failure
   *  lands in `phase`, which is what the card reports. */
  save: (file: File, picked: PickedFolder) => Promise<void>;
  reset: () => void;
}

/**
 * Filing a generated protocol into a project.
 *
 * The upload sequence itself is shared with the template forms
 * (`fileIntoProject`) — storage object, then the row that owns it, then the
 * backup. This hook only decides the destination and reports progress.
 */
export function useProtocolSave(params: {
  orgId: string | undefined;
  buildingId: number;
  userId: number | undefined;
}): UseProtocolSaveResult {
  const { orgId, buildingId, userId } = params;
  const queryClient = useQueryClient();
  const documentStore = useDocumentStore();
  const [phase, setPhase] = useState<ProtocolSavePhase>({ phase: "created" });

  const reset = useCallback(() => setPhase({ phase: "created" }), []);

  const save = useCallback(
    async (file: File, picked: PickedFolder) => {
      if (!orgId || !userId) {
        setPhase({ phase: "failed" });
        return;
      }
      setPhase({ phase: "uploading" });
      try {
        await fileIntoProject({
          file,
          orgId,
          buildingId,
          projectId: Number(picked.projectId),
          folderId: picked.folderId ? Number(picked.folderId) : null,
          userId,
          // `documents.source` is a free-text column and nothing branches on
          // it; the two values in use are "direct_upload" and "template_form".
          // A new one would need checking against the database first, and a
          // rejection lands AFTER the object is already in storage.
          source: "direct_upload",
          keyPrefix: "protocols",
        });
        setPhase({
          phase: "saved",
          projectName: picked.projectName,
          folderPath: picked.folderPath,
          inFolder: picked.folderId !== null,
        });
      } catch (error) {
        if (error instanceof UnconfirmedDocumentRegistrationError) {
          setPhase({ phase: "unconfirmed" });
          documentStore.invalidateProjectCache(picked.projectId);
          void queryClient.invalidateQueries({ queryKey: ["documents"] });
          return;
        }
        // The card carries the failure and a Retry; a toast could carry
        // neither, and the console is where a reported failure is diagnosed.
        console.error("Protocol save failed", error);
        setPhase({ phase: "failed" });
      }
    },
    [orgId, buildingId, userId, documentStore, queryClient]
  );

  return { phase, save, reset };
}
